import { isEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { isNotEmpty } from '@/components/sg-design-system/utils/typescript-helpers.ts';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';

export const FixingReference2 = () => {
  const {
    tileId,
    fixingReference2,
    fixingReference2List,
    fixingReference2Default,
    isCrossed,
    crossCurrency,
    currency: { currency1, currency2, currencyPair },
  } = useOptionSelector((option) => ({
    fixingReference2: option.properties.fixingReference2,
    fixingReference2Default: option.properties.fixingReference2Default,
    fixingReference2List: option.properties.fixingReference2List,
    isCrossed: option.properties.isCrossed,
    crossCurrency: option.properties.crossCurrency,
    currency: selectOptionCurrency(option),
    tileId: option.properties.tileId,
  }));

  const isSameReferenceAsDefault = fixingReference2 === fixingReference2Default;
  const [checkOption] = useCheckOption();

  function handleChange(event: React.ChangeEvent<HTMLSelectElement>) {
    return checkOption({
      tileId,
      properties: {
        fixingReference2: event.target.value,
      },
    });
  }

  const isCrossCurrency = isNotEmpty(crossCurrency) && crossCurrency !== currency1 && crossCurrency !== currency2;
  const shouldDisplay = isCrossed && !isEmpty(fixingReference2List) && isCrossCurrency;
  if (!shouldDisplay) {
    return null;
  }

  const currencyPairDisplayed = isCrossCurrency ? `${crossCurrency}/${currency2}` : currencyPair;
  return (
    <OptionFormGroup
      label={
        <FormattedMessage id={'option.fields.fixingReference'} values={{ currencyPair: currencyPairDisplayed ?? '' }} />
      }
      addon={
        !isSameReferenceAsDefault ? (
          <Tooltip
            position="right"
            contents={
              <FormattedMessage
                id="option.fields.fixingReference.info"
                values={{ defaultFixingReference: fixingReference2Default, br: <br /> }}
              />
            }
          >
            <div className="badge badge-dismissible">
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
            </div>
          </Tooltip>
        ) : undefined
      }
    >
      <Form.Select
        value={fixingReference2 ?? fixingReference2Default}
        onChange={handleChange}
        size="sm"
        className={`${!isSameReferenceAsDefault ? 'text-warning fw-bold' : ''}`}
        data-e2e="fixing-reference2-selector"
      >
        {fixingReference2List?.map((ref) => (
          <option className="text-primary" key={ref} value={ref}>
            {ref}
          </option>
        ))}
      </Form.Select>
    </OptionFormGroup>
  );
};
